import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import styles from './Banner.module.scss'; // Create a CSS/SCSS module for styles
import { CommandCenterBannerType } from './types';
import { useResponsive } from '@/hooks';
import { getCommandCenterBanner } from '@/lib/apis';
import { getDirectusImageUrl } from '@/utils/getUrl';
import { Typography } from '@/components/Atoms';
import classNames from 'classnames';
import BannerCountdown from './BannerCountdown';
import BannerContent from './BannerContent';

type CommandCenterBannerProps = {};

const MediaBanner: React.FC<CommandCenterBannerProps> = () => {
  const { isDesktop, isDesktop1024 } = useResponsive();
  const [data, setData] = useState<CommandCenterBannerType | null>(null);

  const fetchData = async () => {
    const bannerData = await getCommandCenterBanner();
    setData(bannerData as unknown as CommandCenterBannerType);
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (!data) return null;

  const mobile = data?.bg_img_mobile || data?.bg_img_tablet;
  const bgImageId = !isDesktop1024 ? data?.bg_img_desktop : mobile;

  const bgVideoId = !isDesktop1024 ? data?.bg_video_id_desktop : data?.bg_video_id_mobile;

  const bgGifId = !isDesktop1024 ? data?.bg_gif_id_desktop : data?.bg_gif_id_mobile;

  const hasButton =
    (data?.countdown && data?.countdown_type === 'end') ||
    (data?.button_link && !data?.countdown) ||
    (data?.countdown && data?.countdown_type === 'start' && Math.max(0, new Date(data?.countdown).getTime() - new Date().getTime()) < 0);

  return (
    <div
      className={styles.banner}
      style={{
        flexDirection: data?.content_position === 'left' ? 'row-reverse' : 'row',
      }}
    >
      {bgVideoId ? (
        <video autoPlay muted loop playsInline preload='none' className='absolute top-0 left-0 object-cover w-full h-full'>
          <source src={getDirectusImageUrl(bgVideoId)} type='video/mp4' />
          Your browser does not support the video tag.
        </video>
      ) : (
        (bgGifId || bgImageId) && (
          <Image
            key={`background-${data?.title?.[0]?.text}`}
            src={getDirectusImageUrl(bgGifId || bgImageId)}
            layout='fill'
            width={'100%'}
            height={'100%'}
            objectFit='cover'
            // todo fix it
            objectPosition='left'
            alt='Background Image'
            className={!(isDesktop || data.bg_brightness === 'darker') ? '' : styles['darkened-image']}
          />
        )
      )}
      <div
        className={`${styles.mainContent}  ${
          data?.countdown || data?.button_text || data?.text_content?.length > 0 ? 'h-full justify-between' : 'justify-center'
        }`}
      >
        <div className=''>
          <div className='flex flex-col justify-center w-full mx-auto gap-x-1 lg:justify-start '>
            {data?.title?.map((t, index) => (
              <Typography
                key={index}
                Component={'h2'}
                size={t.size || 's36ToS22'}
                style={{ color: t.color || 'white' }}
                className={classNames('font-bold text-shadow-xl z-10 text-center lg:text-start xl:leading-10 pb-1')}
              >
                {t.text}
              </Typography>
            ))}
          </div>
          <div className='pb-2'>
            {data?.sub_title && (
              <Typography Component={'p'} size={'s24ToS16'} className={`text-center lg:text-start `}>
                {data?.sub_title}
              </Typography>
            )}{' '}
          </div>
          <div className='flex flex-col flex-wrap justify-center w-full mx-auto gap-x-1 lg:justify-start'>
            {data?.description?.map((t, index) => (
              <div key={index}>
                <Typography
                  key={index}
                  Component={'h2'}
                  size={'s18ToS14'}
                  style={{ color: t.color || 'white' }}
                  className={classNames('font-bold text-shadow-xl z-10 text-center lg:text-start')}
                >
                  {t.text}
                </Typography>
              </div>
            ))}
          </div>
        </div>
        <div className='flex flex-col items-center gap-1 xl:flex-row lg:gap-2 lg:items-end'>
          <div>
            {data?.text_content?.length > 0 && (
              <div className='flex flex-wrap justify-center w-full mx-auto gap-x-1 lg:justify-start'>
                <div className={classNames('font-bold text-shadow-xl z-10 text-center lg:text-start')}>
                  {data?.text_content?.map((t, index) => (
                    <Typography
                      size={t.size === 'large' ? 's22ToS14' : t.size === 'medium' ? 's18ToS14' : 's14ToS12'}
                      key={index}
                      style={{ color: t.color || 'white' }}
                      className={index === 0 ? 'md:block' : ''}
                    >
                      {t.text}
                    </Typography>
                  ))}
                </div>
              </div>
            )}
          </div>
          <BannerCountdown
            targetTime={new Date(data?.countdown)}
            mode={data?.countdown_type}
            hasLabel={data?.countdown_has_label === 'yes'}
            showCountdown={data?.show_countdown}
            size={data?.countdown_size}
            // textColor={data.countdown_color}
            // bgColor={data.countdown_background_color}
          />
          {!hasButton && (
            <a
              href={data?.button_link}
              style={{
                backgroundColor: data?.button_bg_color,
                color: data?.button_text_color,
              }}
              className={styles.button}
            >
              {data?.button_text}
            </a>
          )}
        </div>
        {data?.content_position === 'left' && <BannerContent content={data?.content} />}
      </div>
      <div className='flex-1 flex flex-col items-center justify-center z-[1]'>
        {data?.right_image && (
          <div className={styles.rightImage}>
            <img width={'100%'} height={'100%'} src={getDirectusImageUrl(data?.right_image)} alt='Banner Image' />
          </div>
        )}
        {data?.content_position === 'right' && <BannerContent content={data?.content} />}
      </div>
    </div>
  );
};

export default MediaBanner;
